import { useEffect, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import {
  DOCUMENT_RULE_KINDS,
  validationRulesApi,
  type ValidationRule,
  type ValidationRuleKind,
  type ValidationRuleScope,
  type ValidationRuleSeverity,
} from '../api/validationRules'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  FormSection,
} from '../components/ui'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'

/** F-100: avviso dopo una clonazione. */
const CLONE_NOTICE = 'Copia creata: è inattiva (in bozza). Controllala e attivala quando è pronta.'

/**
 * F-056 (TASK-0310): regole di completezza della pratica. L'Admin decide quali documenti servono
 * (per tutte le pratiche, per un prodotto o per un reparto) e con quale gravita'; il motore di
 * completezza e la coda eccezioni le applicano. Lettura per il backoffice, scrittura solo Admin
 * (il server risponde 403 agli altri: qui si nascondono solo i pulsanti).
 */
const KIND_LABELS: Record<ValidationRuleKind, string> = {
  ProdottoCollegato: 'Prodotto collegato',
  DataDiFirma: 'Data di firma presente',
  ClienteCollegato: 'Cliente collegato',
  ContattoCollegato: 'Contatto collegato',
  RepartoCollegato: 'Reparto collegato',
  ImportoAttesoPresente: 'Importo atteso presente',
  NumeroMinimoRighe: 'Numero minimo di righe',
  PartitaIvaPresente: 'Partita IVA presente',
  IbanPresente: 'IBAN presente e valido',
  CertificatoValido: 'Certificato valido',
  DocumentoNonScaduto: 'Documento non scaduto',
  CompatibilitaCampi: 'Compatibilita fra campi',
  DocumentoPresente: 'Documento presente',
}

/** I tipi che si possono creare: quelli che il motore sa davvero valutare. */
const BASE_CREATABLE_KINDS: ValidationRuleKind[] = [
  'DocumentoPresente',
  'DocumentoNonScaduto',
  'ProdottoCollegato',
  'DataDiFirma',
  'ClienteCollegato',
  'ContattoCollegato',
  'RepartoCollegato',
  'ImportoAttesoPresente',
  'NumeroMinimoRighe',
]

/** F-160: i quattro tipi resi valutabili (partita IVA, IBAN, certificato, compatibilita). */
const EXTENDED_KINDS: ValidationRuleKind[] = ['PartitaIvaPresente', 'IbanPresente', 'CertificatoValido', 'CompatibilitaCampi']

const SCOPE_LABELS: Record<ValidationRuleScope, string> = {
  TuttiIContratti: 'Tutte le pratiche',
  Prodotto: 'Un prodotto',
  Reparto: 'Un reparto',
}

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

interface FormState {
  id?: number
  code: string
  label: string
  kind: ValidationRuleKind
  scope: ValidationRuleScope
  scopeId: string
  severity: ValidationRuleSeverity
  weight: string
  threshold: string
  requiredDocumentType: string
  isActive: boolean
}

const EMPTY_FORM: FormState = {
  code: '',
  label: '',
  kind: 'DocumentoPresente',
  scope: 'TuttiIContratti',
  scopeId: '',
  severity: 'Bloccante',
  weight: '10',
  threshold: '',
  requiredDocumentType: '',
  isActive: true,
}

function scopeText(r: ValidationRule): string {
  if (r.scope === 'Prodotto') return `Prodotto #${r.scopeProductId}`
  if (r.scope === 'Reparto') return `Reparto #${r.scopeDepartmentId}`
  return SCOPE_LABELS.TuttiIContratti
}

export function ValidationRulesPage({ auth }: { auth: LoginResponse }) {
  const canWrite = isAdminRole(auth)
  const extendedOn = useFeatureFlag('pratiche.regole_estese')
  const CREATABLE_KINDS = extendedOn ? [...BASE_CREATABLE_KINDS, ...EXTENDED_KINDS] : BASE_CREATABLE_KINDS
  const [rules, setRules] = useState<ValidationRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  const isEdit = form.id !== undefined
  const isDocumentKind = DOCUMENT_RULE_KINDS.includes(form.kind) || form.kind === 'CertificatoValido'
  const documentTypeRequired = form.kind === 'DocumentoPresente' || form.kind === 'CertificatoValido'

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setRules(await validationRulesApi.list(false))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function openCreate() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  function openEdit(r: ValidationRule) {
    setForm({
      id: r.id,
      code: r.code,
      label: r.label,
      kind: r.kind,
      scope: r.scope,
      scopeId: String(r.scopeProductId ?? r.scopeDepartmentId ?? ''),
      severity: r.severity,
      weight: String(r.weight),
      threshold: r.threshold === null ? '' : String(r.threshold),
      requiredDocumentType: r.requiredDocumentType ?? '',
      isActive: r.isActive,
    })
    setModalError(null)
    setShowModal(true)
  }

  // F-100: dopo "Clona" la copia (inattiva) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(rules, r => openEdit(r))

  function closeModal() {
    if (saving) return
    setShowModal(false)
    setModalError(null)
  }

  async function handleSave() {
    const weight = Number.parseInt(form.weight, 10)
    if (!form.label.trim()) return setModalError('L\'etichetta e\' obbligatoria')
    if (!isEdit && !form.code.trim()) return setModalError('Il codice e\' obbligatorio')
    if (!Number.isInteger(weight) || weight < 1) return setModalError('Il peso deve essere almeno 1')
    if (isDocumentKind && documentTypeRequired && !form.requiredDocumentType.trim())
      return setModalError('Indica il tipo di documento richiesto')
    const scopeId = Number.parseInt(form.scopeId, 10)
    if (!isEdit && form.scope !== 'TuttiIContratti' && !(scopeId > 0))
      return setModalError(form.scope === 'Prodotto' ? 'Indica l\'id del prodotto' : 'Indica l\'id del reparto')

    const threshold = form.threshold.trim() === '' ? null : Number.parseInt(form.threshold, 10)
    const documentType = isDocumentKind ? form.requiredDocumentType.trim() || null : null

    setSaving(true)
    setModalError(null)
    try {
      if (isEdit) {
        await validationRulesApi.update(form.id as number, {
          label: form.label.trim(),
          severity: form.severity,
          weight,
          threshold: form.kind === 'NumeroMinimoRighe' ? threshold : null,
          requiredDocumentType: documentType,
          isActive: form.isActive,
        })
      } else {
        await validationRulesApi.create({
          code: form.code.trim(),
          label: form.label.trim(),
          kind: form.kind,
          scope: form.scope,
          severity: form.severity,
          weight,
          scopeProductId: form.scope === 'Prodotto' ? scopeId : null,
          scopeDepartmentId: form.scope === 'Reparto' ? scopeId : null,
          threshold: form.kind === 'NumeroMinimoRighe' ? threshold : null,
          requiredDocumentType: documentType,
          isActive: form.isActive,
        })
      }
      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(r: ValidationRule) {
    setError(null)
    try {
      await validationRulesApi.setActive(r.id, !r.isActive)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'aggiornamento')
    }
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title="Regole di completezza"
        icon="rule"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          canWrite ? (
            <Button variant="filled" icon="add" onClick={openCreate}>
              Nuova regola
            </Button>
          ) : undefined
        }
      />

      <Callout tone="info">
        Quali informazioni e quali documenti servono perche&apos; una pratica sia completa. Una regola non
        soddisfatta abbassa la completezza e, per i documenti, compare nella coda eccezioni come
        &quot;Documento mancante&quot; o &quot;Documento scaduto&quot;.
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}
      {cloneNotice && (
        <Callout
          tone="info"
          action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setCloneNotice('')} />}
        >
          {cloneNotice}
        </Callout>
      )}

      <Card variant="outlined" padding="none">
        {rules.length === 0 ? (
          <EmptyState icon="rule" title="Nessuna regola configurata." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Etichetta</th>
                <th>Controllo</th>
                <th>Tipo documento</th>
                <th>Si applica a</th>
                <th>Gravita&apos;</th>
                <th>Peso</th>
                <th>Stato</th>
                {canWrite && <th>Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id}>
                  <td className="md-typescale-emphasized-body-medium">{r.code}</td>
                  <td>{r.label}</td>
                  <td>{KIND_LABELS[r.kind] ?? r.kind}</td>
                  <td>{r.requiredDocumentType ?? '—'}</td>
                  <td>{scopeText(r)}</td>
                  <td>
                    <StatusPill tone={r.severity === 'Bloccante' ? 'error' : 'warning'}>
                      {r.severity}
                    </StatusPill>
                  </td>
                  <td className="md-num">{r.weight}</td>
                  <td>
                    <StatusPill tone={r.isActive ? 'success' : 'neutral'}>
                      {r.isActive ? 'Attiva' : 'Disattiva'}
                    </StatusPill>
                  </td>
                  {canWrite && (
                    <td className="text-nowrap">
                      <CloneConfigurationButton
                        entityLabel="regola di completezza"
                        url={`/api/validation-rules/${r.id}/clone`}
                        defaultName={r.label}
                        extraFields={[{ key: 'code', label: 'Codice della copia', required: true, placeholder: 'es. DOC_IDENTITA_2' }]}
                        note="Si copiano controllo, ambito, gravità, peso e tipo di documento. La copia nasce disattiva: il codice deve essere nuovo."
                        onCloned={async id => { await load(); setCloneNotice(CLONE_NOTICE); openAfterClone(id) }}
                      />{' '}
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(r)} />{' '}
                      <IconButton
                        icon={r.isActive ? 'toggle_on' : 'toggle_off'}
                        size="sm"
                        aria-label={r.isActive ? 'Disattiva' : 'Attiva'}
                        onClick={() => void toggleActive(r)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {canWrite && <RuleLifecyclePanel family="validation" onChanged={() => void load()} />}

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        dismissible={!saving}
        title={isEdit ? 'Modifica regola' : 'Nuova regola'}
        actions={
          <>
            <Button variant="text" onClick={closeModal} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}

          <FormSection title="Regola">
            <TextField
              className="md-span-full"
              id="vr-code"
              label="Codice *"
              type="text"
              maxLength={50}
              value={form.code}
              supportingText={isEdit ? 'Codice, controllo e ambito non si cambiano: per cambiarli, disattiva la regola e creane una nuova.' : undefined}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              disabled={saving || isEdit}
            />

            <TextField
              className="md-span-full"
              id="vr-label"
              label="Etichetta *"
              type="text"
              maxLength={255}
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              disabled={saving}
            />
          </FormSection>

          <FormSection title="Controllo" columns={1}>
            <Select
              id="vr-kind"
              label="Controllo *"
              value={form.kind}
              onChange={e => setForm(f => ({ ...f, kind: e.target.value as ValidationRuleKind }))}
              disabled={saving || isEdit}
            >
              {(isEdit && !CREATABLE_KINDS.includes(form.kind) ? [form.kind, ...CREATABLE_KINDS] : CREATABLE_KINDS).map(k => (
                <option key={k} value={k}>{KIND_LABELS[k]}</option>
              ))}
            </Select>

            {isDocumentKind && (
              <TextField
                id="vr-doctype"
                label={`Tipo documento${documentTypeRequired ? ' *' : ' (facoltativo)'}`}
                type="text"
                maxLength={100}
                value={form.requiredDocumentType}
                onChange={e => setForm(f => ({ ...f, requiredDocumentType: e.target.value }))}
                disabled={saving}
                supportingText={
                  (documentTypeRequired
                    ? 'Deve esserci almeno un documento con questo tipo agganciato alla pratica (maiuscole e spazi non contano).'
                    : 'Se vuoto, si controllano tutti i documenti della pratica che hanno una scadenza. Il giorno di scadenza e\' ancora valido.') +
                  " Esempio: Carta d'identita'"
                }
              />
            )}

            {form.kind === 'NumeroMinimoRighe' && (
              <TextField
                id="vr-threshold"
                label="Minimo righe"
                type="number"
                min={1}
                supportingText="es. 3"
                value={form.threshold}
                onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))}
                disabled={saving}
              />
            )}
          </FormSection>

          <FormSection title="Ambito e gravità">
            <Select
              id="vr-scope"
              label="Si applica a"
              value={form.scope}
              onChange={e => setForm(f => ({ ...f, scope: e.target.value as ValidationRuleScope, scopeId: '' }))}
              disabled={saving || isEdit}
            >
              {(Object.keys(SCOPE_LABELS) as ValidationRuleScope[]).map(s => (
                <option key={s} value={s}>{SCOPE_LABELS[s]}</option>
              ))}
            </Select>
            {form.scope !== 'TuttiIContratti' && (
              <TextField
                id="vr-scopeid"
                label={form.scope === 'Prodotto' ? 'Id prodotto' : 'Id reparto'}
                type="number"
                min={1}
                value={form.scopeId}
                onChange={e => setForm(f => ({ ...f, scopeId: e.target.value }))}
                disabled={saving || isEdit}
              />
            )}

            <Select
              id="vr-severity"
              label="Gravita'"
              value={form.severity}
              onChange={e => setForm(f => ({ ...f, severity: e.target.value as ValidationRuleSeverity }))}
              disabled={saving}
            >
              <option value="Bloccante">Bloccante</option>
              <option value="Avviso">Avviso</option>
            </Select>
            <TextField
              id="vr-weight"
              label="Peso (minimo 1)"
              type="number"
              min={1}
              value={form.weight}
              onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
              disabled={saving}
            />
          </FormSection>

          <FormSection title="Opzioni" columns={1}>
            <Checkbox
              id="vr-active"
              label="Attiva"
              checked={form.isActive}
              onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
              disabled={saving}
            />
          </FormSection>
        </>
      </Dialog>
    </Page>
  )
}
